import { useMemo, useState } from "react";
import { useThemeSlice } from "../../../components/ui/StatusBadge.jsx";
import { ModalShellHeader } from "../../../components/modal/ModalShellHeader.jsx";
import { buildModalShellTokens } from "../../../components/modal/modalShellTheme.js";
import { Ico } from "../../../components/icons/icons.jsx";

export function ServiceAccountKeysModal({ open, title, subtitle, publicKey, privateKey, onClose }) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const ap = useMemo(() => buildModalShellTokens(dark, c, A), [dark, c, A]);
  const [copiedField, setCopiedField] = useState(null);

  if (!open) return null;

  const copy = async (field, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 72, background: ap.overlay, backdropFilter: "blur(8px)" }} role="presentation" />
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="service-account-keys-title"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(520px, 96vw)",
          zIndex: 73,
          background: ap.cardGradient,
          border: `1px solid ${ap.cardBorder}`,
          borderRadius: 14,
          boxShadow: ap.cardShadowLg,
          overflow: "hidden",
        }}
      >
        <ModalShellHeader
          title={title}
          titleId="service-account-keys-title"
          subtitle={subtitle}
          icon={KeyIcon}
          onClose={onClose}
          closeLabel="Close keys dialog"
          dark={dark}
          c={c}
          A={A}
          ap={ap}
        />
        <div style={{ padding: "16px 20px 20px" }}>
          <div style={{ padding: "10px 12px", borderRadius: 8, border: `1px solid #F59E0B40`, background: "#FFFBEB", marginBottom: 14, fontSize: 12, color: "#92400E", lineHeight: 1.5 }}>
            Store the private key securely. You will not be able to view it again in full after closing this dialog.
          </div>

          <KeyBlock label="Public key" value={publicKey} copied={copiedField === "public"} onCopy={() => copy("public", publicKey)} c={c} />
          <KeyBlock label="Private key" value={privateKey} copied={copiedField === "private"} onCopy={() => copy("private", privateKey)} c={c} sensitive />

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
            <button type="button" onClick={onClose} style={{ height: 36, padding: "0 16px", borderRadius: 8, border: "none", background: A, color: "#fff", fontWeight: 700, cursor: "pointer" }}>
              Done
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function KeyBlock({ label, value, copied, onCopy, c, sensitive }) {
  return (
    <label style={{ display: "block", marginBottom: 12 }}>
      <span style={{ display: "block", fontSize: 11, fontWeight: 700, color: c.sub, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {label}
      </span>
      <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
        <code
          style={{
            flex: 1,
            display: "block",
            padding: "10px 12px",
            borderRadius: 8,
            border: `1px solid ${sensitive ? "#EF444440" : c.cardBdr}`,
            background: sensitive ? "#FEF2F2" : c.hover,
            color: c.text,
            fontSize: 12,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            wordBreak: "break-all",
            lineHeight: 1.45,
          }}
        >
          {value}
        </code>
        <button type="button" onClick={onCopy} style={{ height: "auto", minWidth: 72, padding: "0 12px", borderRadius: 8, border: `1px solid ${c.cardBdr}`, background: c.card, color: c.text, fontWeight: 700, fontSize: 11, cursor: "pointer" }}>
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </label>
  );
}

function KeyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m21 2-2 2" />
      <path d="m7.5 16.5 9 18" />
      <path d="M3 21v-4l10.5-10.5a2.12 2.12 0 0 1 3 3L6 20z" />
      <path d="m14 6 4 4" />
    </svg>
  );
}
